import React, {useContext} from 'react';
import PropTypes from 'prop-types';

import CommentCard from '~/src/components/comment/card/CommentCard';
import Link from '~/src/components/link/Link';
import Boomerang from '~/src/components/svg/general/Boomerang';
import CommentInput from '~/src/components/comment/input/CommentInput';
import HtmlText from '~/src/components/htmlText/HtmlText';
import Ad from '~/src/components/ad/Ad';
import CommentSort from '~/src/components/comment/sort/CommentSort';
import CommentReactionSort from '~/src/components/comment/sort/CommentReactionSort';

import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import initPolyglot from '~/src/utilities/libs/polyglot';
import {getPageUrl, getContentUrl} from '~/src/utilities/helpers/link';
import {Hashes} from '~/src/utilities/constants/hash';
import {classNames} from '~/src/utilities/helpers/css';
import {getNextCounter} from '~/src/utilities/helpers/counters';
import {getAdUnitName} from '~/src/utilities/helpers/ad';
import {CommentSortColumn} from '~/src/utilities/constants/api';
import {SettingsContext} from '~/src/components/context/SettingsContext';

import css from './CommentArea.module.scss';
import i18nStrings from './CommentArea.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const renderCommentInput = (id, commentingEnabled) => {
    if (commentingEnabled) {
        return <CommentInput id={id} />;
    }
    return <div className={css.commentsClosed}>{getTranslation('commentsClosed')}</div>;
};

const renderComments = (id, comments, page, commentingEnabled) => {
    return comments.map((element, index) => {
        if (index >= 5 && index % 5 === 0 && page === 'CommentPage') {
            return (
                <div key={index}>
                    <Ad adUnitName={getAdUnitName('inside-full-comment', getNextCounter('inside-full-comment'))} />
                    <CommentCard {...element} commentingEnabled={commentingEnabled} page={page} assetId={id} />
                </div>
            );
        }
        return <CommentCard key={index} {...element} commentingEnabled={commentingEnabled} page={page} assetId={id} />;
    });
};

const CommentArea = ({id, url, title, page, comments = [], totalCount, total = 0, commentingEnabled = false, sortBy = CommentSortColumn.CREATED_AT}) => {
    const displayCount = totalCount && isEnabled(Feature.TOTAL_COMMENTS) ? totalCount : total;

    const {siteArea} = useContext(SettingsContext);
    const contentClasses = classNames(css, 'commentContent', siteArea);
    const hasLoadingError = displayCount > 0 && comments.length === 0;

    return (
        <div className={contentClasses} id={Hashes.COMMENT_SECTION}>
            {page === 'CommentPage' && (
                <Link href={getPageUrl(url)} className={css.articleLink}>
                    <HtmlText as="span" className={css.commentTitle} htmlText={title} />
                </Link>
            )}
            <div className={css.commentCounter}>
                <span>{getTranslation('comment', {smart_count: displayCount})}</span>
            </div>

            {page === 'CommentPage' && (
                <>
                    <div className={css.articleBack}>
                        <Link href={getContentUrl(url)} className={css.articleBackLink}>
                            <Boomerang />
                            <span>{getTranslation('toArticle')}</span>
                        </Link>

                        <CommentSort id={id} sortBy={sortBy} />
                    </div>
                    {sortBy.startsWith(CommentSortColumn.REACTIONS) && (
                        <div className={css.sortByReactions}>
                            <CommentReactionSort contentId={id} sortBy={sortBy} />
                        </div>
                    )}
                </>
            )}

            <div className={css.comments}>
                {hasLoadingError ? (
                    <div className={css.commentsClosed}>{getTranslation('loadingError')}</div>
                ) : (
                    <React.Fragment>
                        {renderCommentInput(id, commentingEnabled)}
                        {renderComments(id, comments, page, commentingEnabled)}
                    </React.Fragment>
                )}
            </div>

            {page === 'ContentPage' && displayCount > 3 && (
                <div className={css.loadMoreComment}>
                    <Link href={getPageUrl(`/comment/${id}`)} className={css.loadMoreCommentButton}>
                        {getTranslation('getAllComments')}
                    </Link>
                </div>
            )}
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    CommentArea.propTypes = {
        comments: PropTypes.arrayOf(
            PropTypes.shape({
                body: PropTypes.string.isRequired,
                createdAt: PropTypes.string.isRequired,
                id: PropTypes.string,
                authorNickname: PropTypes.string.isRequired,
                asset: PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    type: PropTypes.string
                })
            })
        ),
        total: PropTypes.number,
        totalCount: PropTypes.number,
        id: PropTypes.string.isRequired,
        page: PropTypes.string.isRequired,
        title: PropTypes.string,
        url: PropTypes.string,
        commentingEnabled: PropTypes.bool,
        nextLink: PropTypes.string
    };
}

export default CommentArea;
